import { apiClient } from '../apiClient';

export const PATHS = {
  TEAMS_V5: '/api/v5/teams/',
} as const;

// A care team. We only need each team's primary coach and type to know whether
// the member has a coach, so the shape is intentionally minimal.
export type TeamV5 = {
  code: string;
  uuid: string;
  primaryCoach: string | null;
  /**
   * e.g. `care-team`, `care-group-solo`, `care-team-chatbot` (Sparta's TeamType).
   * Always present: webserver's TeamBaseSerializer returns it on list and detail.
   */
  teamType: string;
};

type TeamsV5Response = { objects?: TeamV5[]; items?: TeamV5[] } | TeamV5[];

// Mirrors Sparta's getTeamsV5: GET /api/v5/teams/. Used to gate coach matching.
export async function fetchTeamsV5(signal?: AbortSignal): Promise<TeamV5[]> {
  const res = await apiClient.get<TeamsV5Response>(PATHS.TEAMS_V5, { signal });
  const data = res.data;
  if (Array.isArray(data)) return data;
  return data?.objects ?? data?.items ?? [];
}

/**
 * Whether the user has completed coach matching: the solo team (matched by the
 * customer profile's `soloTeamCode`) has a `primaryCoach`. Mirrors Sparta's
 * checkCoachEnrollmentEpic. A missing soloTeamCode or no matching team → false.
 * TODO: relying on solo team is likely a bug: after onboarding, the solo team
 * is not guaranteed to exist or to be in the list of teams. Longtime members
 * may reopen a link to onboarding, and if they've since been removed from
 * their solo team, they would get stuck in provider matching. If the solo team
 * is not found, we should accept any team of types 'care-team' or 'care-team-solo'
 * that has a primary provider.
 */
export function hasPrimaryCoach(teams: TeamV5[], soloTeamCode: string | undefined): boolean {
  if (!soloTeamCode) return false;
  return Boolean(teams.find((team) => team.code === soloTeamCode)?.primaryCoach);
}
